import React, { useState, useEffect } from 'react';
import { Input, Select, Space } from 'antd';
import { PhoneOutlined } from '@ant-design/icons';

const { Option } = Select;

// Country codes with flags and phone patterns
const countryCodes = [
  { code: '+30', country: 'GR', flag: '🇬🇷', name: 'Greece', pattern: /^[0-9]{10}$/, placeholder: '6912345678' },
  { code: '+357', country: 'CY', flag: '🇨🇾', name: 'Cyprus', pattern: /^[0-9]{8}$/, placeholder: '99123456' },
  { code: '+1', country: 'US', flag: '🇺🇸', name: 'USA', pattern: /^[0-9]{10}$/, placeholder: '2025551234' },
  { code: '+44', country: 'GB', flag: '🇬🇧', name: 'UK', pattern: /^[0-9]{10,11}$/, placeholder: '7911123456' },
  { code: '+49', country: 'DE', flag: '🇩🇪', name: 'Germany', pattern: /^[0-9]{10,11}$/, placeholder: '15123456789' },
  { code: '+33', country: 'FR', flag: '🇫🇷', name: 'France', pattern: /^[0-9]{9}$/, placeholder: '612345678' },
  { code: '+39', country: 'IT', flag: '🇮🇹', name: 'Italy', pattern: /^[0-9]{9,10}$/, placeholder: '3123456789' },
  { code: '+34', country: 'ES', flag: '🇪🇸', name: 'Spain', pattern: /^[0-9]{9}$/, placeholder: '612345678' },
  { code: '+31', country: 'NL', flag: '🇳🇱', name: 'Netherlands', pattern: /^[0-9]{9}$/, placeholder: '612345678' },
  { code: '+32', country: 'BE', flag: '🇧🇪', name: 'Belgium', pattern: /^[0-9]{9}$/, placeholder: '471234567' },
  { code: '+43', country: 'AT', flag: '🇦🇹', name: 'Austria', pattern: /^[0-9]{10,11}$/, placeholder: '6641234567' },
  { code: '+41', country: 'CH', flag: '🇨🇭', name: 'Switzerland', pattern: /^[0-9]{9}$/, placeholder: '791234567' },
  { code: '+48', country: 'PL', flag: '🇵🇱', name: 'Poland', pattern: /^[0-9]{9}$/, placeholder: '512345678' },
  { code: '+40', country: 'RO', flag: '🇷🇴', name: 'Romania', pattern: /^[0-9]{9}$/, placeholder: '721234567' },
  { code: '+359', country: 'BG', flag: '🇧🇬', name: 'Bulgaria', pattern: /^[0-9]{9}$/, placeholder: '888123456' },
  { code: '+381', country: 'RS', flag: '🇷🇸', name: 'Serbia', pattern: /^[0-9]{8,9}$/, placeholder: '601234567' },
  { code: '+385', country: 'HR', flag: '🇭🇷', name: 'Croatia', pattern: /^[0-9]{8,9}$/, placeholder: '911234567' },
  { code: '+386', country: 'SI', flag: '🇸🇮', name: 'Slovenia', pattern: /^[0-9]{8}$/, placeholder: '31123456' },
  { code: '+355', country: 'AL', flag: '🇦🇱', name: 'Albania', pattern: /^[0-9]{9}$/, placeholder: '672123456' },
  { code: '+389', country: 'MK', flag: '🇲🇰', name: 'North Macedonia', pattern: /^[0-9]{8}$/, placeholder: '70123456' },
  { code: '+90', country: 'TR', flag: '🇹🇷', name: 'Turkey', pattern: /^[0-9]{10}$/, placeholder: '5321234567' },
];

/**
 * Phone input component with country code selector
 * Returns full phone number with country code (e.g., "+306912345678")
 */
const PhoneInput = ({ value, onChange, placeholder, disabled }) => {
  const [countryCode, setCountryCode] = useState('+30'); // Default to Greece
  const [phoneNumber, setPhoneNumber] = useState('');

  // Parse initial value if provided
  useEffect(() => {
    if (value) {
      // Try to extract country code from value
      const matchedCountry = countryCodes.find(c => value.startsWith(c.code));
      if (matchedCountry) {
        setCountryCode(matchedCountry.code);
        setPhoneNumber(value.slice(matchedCountry.code.length));
      } else if (value.startsWith('+')) {
        // Unknown country code, keep as is
        const codeMatch = value.match(/^\+\d{1,4}/);
        if (codeMatch) {
          setCountryCode(codeMatch[0]);
          setPhoneNumber(value.slice(codeMatch[0].length));
        }
      } else {
        // No country code, assume it's just the number
        setPhoneNumber(value);
      }
    }
  }, []);

  // Get current country info
  const currentCountry = countryCodes.find(c => c.code === countryCode) || countryCodes[0];

  // Handle country code change
  const handleCountryChange = (newCode) => {
    setCountryCode(newCode);
    // Trigger onChange with updated full number
    if (onChange && phoneNumber) {
      onChange(newCode + phoneNumber);
    }
  };

  // Handle phone number change
  const handlePhoneChange = (e) => {
    // Only allow digits
    const cleanNumber = e.target.value.replace(/\D/g, '');
    setPhoneNumber(cleanNumber);

    // Trigger onChange with full number including country code
    if (onChange) {
      onChange(cleanNumber ? countryCode + cleanNumber : '');
    }
  };

  return (
    <Space.Compact style={{ width: '100%' }}>
      <Select
        value={countryCode}
        onChange={handleCountryChange}
        style={{ width: 100 }}
        disabled={disabled}
        showSearch
        optionFilterProp="children"
        filterOption={(input, option) => {
          const country = countryCodes.find(c => c.code === option.value);
          return country && (
            country.name.toLowerCase().includes(input.toLowerCase()) ||
            country.code.includes(input) ||
            country.country.toLowerCase().includes(input.toLowerCase())
          );
        }}
      >
        {countryCodes.map((country) => (
          <Option key={country.code} value={country.code}>
            <span style={{ marginRight: 4 }}>{country.flag}</span>
            {country.code}
          </Option>
        ))}
      </Select>
      <Input
        value={phoneNumber}
        onChange={handlePhoneChange}
        placeholder={placeholder || currentCountry.placeholder}
        prefix={<PhoneOutlined />}
        disabled={disabled}
        style={{ flex: 1 }}
        maxLength={15}
      />
    </Space.Compact>
  );
};

/**
 * Validate phone number format
 * @param {string} fullNumber - Full phone number with country code (e.g., "+306912345678")
 * @returns {boolean} - Whether the phone number is valid
 */
export const validatePhoneNumber = (fullNumber) => {
  if (!fullNumber) return true; // Empty is valid (optional field)

  // Must start with +
  if (!fullNumber.startsWith('+')) return false;

  // Find matching country
  const matchedCountry = countryCodes.find(c => fullNumber.startsWith(c.code));

  if (matchedCountry) {
    const numberPart = fullNumber.slice(matchedCountry.code.length);
    return matchedCountry.pattern.test(numberPart);
  }

  // For unknown country codes, just check it's digits and reasonable length
  const numberPart = fullNumber.replace(/^\+\d{1,4}/, '');
  return /^[0-9]{6,15}$/.test(numberPart);
};

/**
 * Get country info from phone number
 */
export const getCountryFromPhone = (fullNumber) => {
  if (!fullNumber) return null;
  return countryCodes.find(c => fullNumber.startsWith(c.code)) || null;
};

export { countryCodes };
export default PhoneInput;
